import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Use relative URL for same-origin requests in production
const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadingMicrosoft, setLoadingMicrosoft] = useState(false);

  useEffect(() => {
    // Check for error in URL params
    const error = searchParams.get('error');
    if (error) {
      toast.error(`Login failed: ${error.replace(/_/g, ' ')}`);
    }

    const checkAuth = async () => {
      try {
        const backendUrl = getBackendUrl();
        const response = await fetch(`${backendUrl}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          navigate('/dashboard');
        }
      } catch (error) {
        // Not authenticated
      }
    };
    checkAuth();
  }, [navigate, searchParams]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMicrosoftLogin = async () => {
    setLoadingMicrosoft(true);
    try {
      const backendUrl = getBackendUrl();
      const response = await fetch(`${backendUrl}/api/auth/microsoft/login`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      } else {
        toast.error('Microsoft login not available');
        setLoadingMicrosoft(false);
      }
    } catch (error) {
      toast.error('Failed to initiate Microsoft login');
      setLoadingMicrosoft(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-background p-8">
        <div className="w-full max-w-md backdrop-blur-xl bg-card/50 border border-border rounded-lg p-8" data-testid="login-form-container">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center">
              <span className="text-4xl font-bold" style={{ color: '#00F8B3' }}>x</span>
              <span className="text-4xl font-bold text-white">entra</span>
            </div>
          </div>
          
          <h2 className="text-3xl font-sans font-bold mb-2">Welcome Back</h2>
          <p className="text-muted-foreground mb-8">Access your cyber security portal</p>
          
          <div className="space-y-3">
            <Button 
              onClick={handleGoogleLogin}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,248,179,0.3)] rounded-md font-sans font-medium text-sm py-6"
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <Button 
              onClick={handleMicrosoftLogin}
              disabled={loadingMicrosoft}
              variant="outline"
              className="w-full border-[#00a4ef]/30 hover:bg-[#00a4ef]/10 hover:border-[#00a4ef]/50 rounded-md font-sans font-medium text-sm py-6"
              data-testid="microsoft-login-button"
            >
              {loadingMicrosoft ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#f25022" d="M1 1h10v10H1z"/>
                  <path fill="#00a4ef" d="M1 13h10v10H1z"/>
                  <path fill="#7fba00" d="M13 1h10v10H13z"/>
                  <path fill="#ffb900" d="M13 13h10v10H13z"/>
                </svg>
              )}
              Continue with Microsoft
            </Button>
          </div>
          
          <div className="mt-8 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Secure access powered by Xentra Group
            </p>
          </div>
        </div>
      </div>
      
      <div 
        className="hidden lg:block lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1489436969537-cf0c1dc69cba?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(16, 13, 47, 0.7)' }}></div>
        <div className="absolute inset-0 flex items-center justify-center text-white p-12">
          <div>
            <div className="flex items-center mb-6">
              <span className="text-7xl font-bold" style={{ color: '#00F8B3' }}>x</span>
              <span className="text-7xl font-bold text-white">entra</span>
            </div>
            <p className="text-2xl font-sans">Cyber Security Command Center</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;